import React from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  Divider,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import styles from "./zeroTrustPolicies.module.scss";

const examplePrompts = [
  "Block all external access to OT devices outside business hours (6PM - 6AM)",
  "Require multi-factor authentication for all SCADA system access",
  "Quarantine IoT devices that show anomalous network behavior",
  "Deny write commands to critical PLCs from untrusted network zones",
  "Restrict administrative access to production systems during weekends",
];

const generatedPolicy = {
  title: "AI-Generated Zero Trust Policy",
  scope: "OT Production Network",
  confidence: "94%",
  conditions: [
    "Time window: Outside business hours",
    "Device type: PLCs, SCADA Controllers, HMI Systems",
    "Action type: Write commands, Configuration changes",
    "Source: External networks, Untrusted zones",
  ],
  actions: [
    "Block all write operations",
    "Send real-time alert to SOC team",
    "Log event to blockchain audit trail",
    "Trigger automated investigation workflow",
  ],
  impact: "47 assets will be impacted by this policy",
};

const AiPolicyAssistantDialog = ({
  open,
  prompt,
  onClose,
  onPromptChange,
  onSelectPrompt,
  onGenerate,
  onRegenerate,
  showGenerated,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ className: styles.aiDialog }}
    >
      <Box className={styles.aiDialogHeader}>
        <Box className={styles.aiDialogHeaderLeft}>
          <Box className={styles.aiDialogIcon}>
            <AutoAwesomeOutlinedIcon />
          </Box>
          <Box>
            <Typography className={styles.aiDialogTitle}>
              AI Policy Assistant
            </Typography>
            <Typography className={styles.aiDialogSubtitle}>
              Describe the policy you want to enforce in natural language
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} className={styles.aiDialogClose}>
          <CloseRoundedIcon />
        </IconButton>
      </Box>

      <Box className={styles.aiDialogBody}>
        {!showGenerated ? (
          <>
            <Typography className={styles.aiSectionTitle}>
              Policy Instructions
            </Typography>
            <TextField
              multiline
              minRows={4}
              fullWidth
              sx={{ borderRadius: 2 }}
              placeholder="Example: Create a Zero Trust policy for OT devices that blocks write commands outside business hours..."
              value={prompt}
              onChange={(event) => onPromptChange(event.target.value)}
              className={styles.aiTextarea}
            />

            <Box className={styles.aiExamplesCard}>
              <Box className={styles.aiExamplesHeader}>
                <LightbulbOutlinedIcon fontSize="small" />
                <Typography className={styles.aiExamplesTitle}>
                  Example Prompts
                </Typography>
              </Box>
              <Box className={styles.aiExamplesList}>
                {examplePrompts.map((item) => (
                  <button
                    type="button"
                    key={item}
                    className={styles.aiExampleItem}
                    onClick={() => onSelectPrompt(item)}
                  >
                    {item}
                  </button>
                ))}
              </Box>
            </Box>
          </>
        ) : (
          <Box className={styles.aiGeneratedCard}>
            <Box className={styles.aiGeneratedHeader}>
              <Box>
                <Typography className={styles.aiGeneratedTitle}>
                  {generatedPolicy.title}
                </Typography>
                <Typography className={styles.aiGeneratedScope}>
                  Scope: {generatedPolicy.scope}
                </Typography>
              </Box>
              <Chip
                label={`${generatedPolicy.confidence} Confidence`}
                icon={<CheckCircleRoundedIcon />}
                className={styles.aiConfidenceChip}
              />
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box className={styles.aiGeneratedGrid}>
              <Box>
                <Typography className={styles.aiGeneratedHeading}>
                  Conditions
                </Typography>
                <ul className={styles.aiGeneratedList}>
                  {generatedPolicy.conditions.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </Box>
              <Box>
                <Typography className={styles.aiGeneratedHeading}>
                  Enforcement Actions
                </Typography>
                <ul className={styles.aiGeneratedList}>
                  {generatedPolicy.actions.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography className={styles.aiImpact}>
              {generatedPolicy.impact}
            </Typography>
          </Box>
        )}
      </Box>

      <Box className={styles.aiDialogFooter}>
        <Button onClick={onClose} className={styles.aiCancelButton}>
          Cancel
        </Button>
        {!showGenerated ? (
          <Button
            variant="contained"
            className={styles.aiPrimaryButton}
            onClick={onGenerate}
          >
            Generate Policy
          </Button>
        ) : (
          <Box className={styles.aiFooterActions}>
            <Button
              variant="outlined"
              className={styles.aiSecondaryButton}
              onClick={onRegenerate}
            >
              Regenerate Policy
            </Button>
            <Button
              variant="contained"
              onClick={onClose}
              className={styles.aiPrimaryButton}
            >
              Apply Policy
            </Button>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default AiPolicyAssistantDialog;
